"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "motion/react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { useChart } from "./chart-context";
export interface XAxisProps {
  /** Number of ticks to show (including first and last). Default: 5 */
  numTicks?: number;
  /** Width of the date ticker box for fade calculation. Default: 50 */
  tickerHalfWidth?: number;
}
interface XAxisLabelProps {
  label: string;
  x: number;
  crosshairX: number | null;
  isHovering: boolean;
  tickerHalfWidth: number;
}
function XAxisLabel(t0) {
  const $ = _c(13);
  const {
    label,
    x,
    crosshairX,
    isHovering,
    tickerHalfWidth
  } = t0;
  const fadeRadius = tickerHalfWidth + 20;
  let opacity = 1;
  if (isHovering && crosshairX !== null) {
    const distance = Math.abs(x - crosshairX);
    if (distance < tickerHalfWidth) {
      opacity = 0;
    } else {
      if (distance < fadeRadius) {
        opacity = (distance - tickerHalfWidth) / 20;
      }
    }
  }
  let t1;
  if ($[0] !== x) {
    t1 = {
      left: x,
      bottom: 12,
      width: 0,
      display: "flex",
      justifyContent: "center"
    };
    $[0] = x;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== opacity) {
    t2 = {
      opacity
    };
    $[2] = opacity;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("text-chart-label whitespace-nowrap text-xs");
    t4 = {
      opacity: 1
    };
    t5 = {
      duration: 0.4,
      ease: "easeInOut"
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== label || $[8] !== t2) {
    t6 = <motion.span animate={t2} className={t3} initial={t4} transition={t5}>{label}</motion.span>;
    $[7] = label;
    $[8] = t2;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t6) {
    t7 = <div className="absolute" style={t1}>{t6}</div>;
    $[10] = t1;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
export function XAxis({
  numTicks = 5,
  tickerHalfWidth = 50
}: XAxisProps) {
  const {
    xScale,
    margin,
    tooltipData,
    containerRef
  } = useChart();
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  // Sync portal container (adjust state during render)
  if (containerRef.current && containerRef.current !== portalContainer) {
    setPortalContainer(containerRef.current);
  }

  // Generate evenly spaced tick values, always including first and last dates
  const labelsToShow = useMemo(() => {
    const domain = xScale.domain();
    const startDate = domain[0];
    const endDate = domain[1];
    if (!(startDate && endDate)) {
      return [];
    }
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();
    const timeRange = endTime - startTime;

    // Create evenly spaced dates from start to end
    const tickCount = Math.max(2, numTicks); // At least first and last
    const dates: Date[] = [];
    for (let i = 0; i < tickCount; i++) {
      const t = i / (tickCount - 1); // 0 to 1
      const time = startTime + t * timeRange;
      dates.push(new Date(time));
    }
    return dates.map(date => ({
      date,
      x: (xScale(date) ?? 0) + margin.left,
      label: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      })
    }));
  }, [xScale, margin.left, numTicks]);
  const isHovering = tooltipData !== null;
  const crosshairX = tooltipData ? tooltipData.x + margin.left : null;

  // Use portal to render into the chart container
  // Only render after mount on client side
  if (!portalContainer) {
    return null;
  }
  return createPortal(<div className="pointer-events-none absolute inset-0">
      {labelsToShow.map(item => <XAxisLabel crosshairX={crosshairX} isHovering={isHovering} key={`${item.label}-${item.x}`} label={item.label} tickerHalfWidth={tickerHalfWidth} x={item.x} />)}
    </div>, portalContainer);
}
XAxis.displayName = "XAxis";
export default XAxis;